import {Box} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import type {FC} from 'react';
import {routes} from '../../../config/constants';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {isReleasedNewRegisterFlow} from '../../../config/unReleasePage';
import {CommonLink} from '../../../shared/components/CommonLink';
import {SectionTitle} from '../../../shared/components/SectionTitle';
import {getMailAuthLinkWithParamsHooks} from '../../../shared/services/getMailAuthLinkWithParamsHooks';
import {RegisterBodyLegacy} from './RegisterBodyLegacy';
import {RegisterBodyNew} from './RegisterBodyNew';
import {RegisterTocLegacy} from './RegisterTocLegacy';
import {RegisterTocNew} from './RegisterTocNew';

export const FaqPage: FC = () => {
  const router = useRouter();
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.pathname)}`;
  const TEXT_CLASSNAME = 'leading text-black-1 text-base';
  const LINK_CLASSNAME = 'text-link-1 underline hover:opacity-80';
  const TITLE = 'ヘルプ・お問い合わせ';
  const mailAuthLink = getMailAuthLinkWithParamsHooks({});
  const isNewRegisterFlow = isReleasedNewRegisterFlow();

  return (
    <>
      <Head>
        <title key="title">{TITLE}</title>
        <meta key="og:title" property="og:title" content={TITLE} />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta name="keywords" content="外資,就活,ヘルプ,お問い合わせ" />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <meta
          name="description"
          content="外資就活ドットコムのヘルプ・お問い合わせについてです。よくある質問として、会員登録やログイン、メール配信についてなどの情報をまとめています。ご利用いただく中でお困りの際はご確認ください。"
        />
      </Head>
      <Box className="w-full max-w-[664px]" id="fb-root">
        <SectionTitle order={1} title={TITLE} />
        <Box className="mb-16 bg-white px-8 py-16 text-base pc:p-32">
          <p className="mb-12 bg-background-1 px-8 py-4 font-bold leading text-black-1">
            よくある質問
          </p>
          {isNewRegisterFlow ? (
            <RegisterTocNew
              textClassName={TEXT_CLASSNAME}
              linkClassName={LINK_CLASSNAME}
            />
          ) : (
            <RegisterTocLegacy
              textClassName={TEXT_CLASSNAME}
              linkClassName={LINK_CLASSNAME}
            />
          )}
          <p className={`${TEXT_CLASSNAME} text-left`}>◆2.ログインについて◆</p>
          <ul className={`${TEXT_CLASSNAME} text-left`}>
            <li>
              2-1.
              <CommonLink href="#b2" className={LINK_CLASSNAME}>
                ログインできない
              </CommonLink>
            </li>
            <li>
              2-2.
              <CommonLink href="#b1" className={LINK_CLASSNAME}>
                パスワードを忘れた・変更したい
              </CommonLink>
            </li>
          </ul>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            ◆3.プロフィールについて◆
          </p>
          <ul className={`${TEXT_CLASSNAME} text-left`}>
            <li>
              3-1.
              <CommonLink href="#c1" className={LINK_CLASSNAME}>
                プロフィールを変更したい
              </CommonLink>
            </li>
            <li>
              3-2.
              <CommonLink href="#c2" className={LINK_CLASSNAME}>
                最終学歴の更新をしたい
              </CommonLink>
            </li>
          </ul>
          <p className={`${TEXT_CLASSNAME} text-left`}>
            ◆4.メール配信について◆
          </p>
          <ul className={`${TEXT_CLASSNAME} text-left`}>
            <li>
              4-1.
              <CommonLink href="#d1" className={LINK_CLASSNAME}>
                内定が出て、就職活動を終えたので就活生向けのメールを止めたい
              </CommonLink>
            </li>
            <li>
              4-2.
              <CommonLink href="#d2" className={LINK_CLASSNAME}>
                メールマガジンの配信停止をしたい
              </CommonLink>
            </li>
            <li>
              4-3.
              <CommonLink href="#d3" className={LINK_CLASSNAME}>
                配信先メールアドレスを変更したい
              </CommonLink>
            </li>
          </ul>
          <p className={`${TEXT_CLASSNAME} text-left`}>◆5.機能について◆</p>
          <ul className={`${TEXT_CLASSNAME} text-left`}>
            <li>
              5-1.
              <CommonLink href="#e1" className={LINK_CLASSNAME}>
                外資就活ドットコムの使い方がわからない
              </CommonLink>
            </li>
            <li>
              5-2.
              <CommonLink href="#e2" className={LINK_CLASSNAME}>
                エントリーしている選考/イベントのキャンセルについて
              </CommonLink>
            </li>
            <li>
              5-3.
              <CommonLink href="#e3" className={LINK_CLASSNAME}>
                退会したい
              </CommonLink>
            </li>
          </ul>
          <p className={`${TEXT_CLASSNAME} text-left`} />

          {isNewRegisterFlow ? (
            <RegisterBodyNew
              linkClassName={LINK_CLASSNAME}
              mailAuthLink={mailAuthLink}
            />
          ) : (
            <RegisterBodyLegacy
              linkClassName={LINK_CLASSNAME}
              mailAuthLink={mailAuthLink}
            />
          )}
          <div className="mt-16 text-right">
            <CommonLink href="#fb-root" className={LINK_CLASSNAME}>
              FAQトップへ
            </CommonLink>
          </div>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            2.ログイン
          </p>
          <div id="b2" className="mt-16">
            <h3>
              <span>Q.</span>ログインできない
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              メールアドレスもしくはパスワードに誤りがあります。メールアドレス、パスワードをご確認後、再度ログインを行なって下さい。登録時にご入力いただいた「いつも使うメールアドレス」からもログイン可能です。
              <br />
              ※ブラウザのキャッシュ&Cookieをクリアして頂いた後、再度ログインを試みていただくことを推奨いたします。
            </p>
            <p>
              パスワードを忘れてしまった場合は
              <CommonLink href="#b1" className={LINK_CLASSNAME}>
                パスワードを忘れた・変更したい
              </CommonLink>
              をご覧ください。
            </p>
          </div>

          <div id="b1" className="mt-16">
            <h3>
              <span>Q.</span>パスワードを忘れた・変更したい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              <CommonLink
                href={routes.REMIND_PASS}
                target="_blank"
                className={LINK_CLASSNAME}
              >
                パスワード変更画面
              </CommonLink>
              より再設定を行なってください。
            </p>
          </div>
          <div className="mt-16 text-right">
            <CommonLink href="#fb-root" className={LINK_CLASSNAME}>
              FAQトップへ
            </CommonLink>
          </div>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            3.プロフィール
          </p>
          <div id="c1" className="mt-16">
            <h3>
              <span>Q.</span>プロフィールを変更したい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              PCをご使用の方は、マイページ左のメニューにあります、「プロフィールの編集」から変更を行なってください。
              アプリ・スマホブラウザをご使用の方は、ログイン後、「各種設定・その他」→「プロフィールの設定・編集」から変更を行なってください。
            </p>
            <p>
              氏名など、プロフィールの一部はweb上からの変更ができません。もし変更をご希望の場合は
              <CommonLink
                href={routes.INQUIRY}
                target="_blank"
                className={LINK_CLASSNAME}
              >
                お問い合わせフォーム
              </CommonLink>
              にて変更箇所と変更内容をお知らせください。
            </p>
          </div>

          <div id="c2" className="mt-16">
            <h3>
              <span>Q.</span>最終学歴の更新をしたい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              毎年1、4、7、10月に更新のご依頼をさせていただいております。
            </p>
            <p>
              PC・スマホブラウザをご使用の方は、ログイン時に『定期プロフィール更新』というフォームが表示されます。
              アプリをご使用の方は、ログイン後、左上メニューバーより「各種設定・その他」→「プロフィールの設定・編集」を選択いただきますと、『定期プロフィール更新』というフォームが表示されます。
            </p>
          </div>
          <div className="mt-16 text-right">
            <CommonLink href="#fb-root" className={LINK_CLASSNAME}>
              FAQトップへ
            </CommonLink>
          </div>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            4.メール配信について
          </p>
          <div id="d1" className="mt-16">
            <h3>
              <span>Q.</span>
              内定が出て、就職活動を終えたので就活生向けのメールを止めたい
            </h3>
          </div>

          <div id="d2" className="mt-16">
            <h3>
              <span>Q.</span>メールの配信停止をしたい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              マイページ左のメニューにあります、「
              <CommonLink
                href={routes.MYPAGE_DM_SETTING}
                className={LINK_CLASSNAME}
              >
                メール受信設定
              </CommonLink>
              」から変更を行なってください。
            </p>
            <p>
              ※お手続き完了後、2-3日後よりダイレクトメールの配信が停止となります。予めご了承ください。
            </p>
          </div>

          <div id="d3" className="mt-16">
            <h3>
              <span>Q.</span>配信先メールアドレスを変更したい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              マイページ左のメニューにあります、「プロフィール設定」より「いつも使うメールアドレス」の修正を行なってください。
            </p>
          </div>

          <div className="mt-16 text-right">
            <CommonLink href="#fb-root" className={LINK_CLASSNAME}>
              FAQトップへ
            </CommonLink>
          </div>

          <p className="mb-12 bg-background-1 px-8 py-4 text-md font-bold leading text-black-1">
            5.機能について
          </p>
          <div id="e1" className="mt-16">
            <h3>
              <span>Q.</span>外資就活ドットコムの使い方がわからない
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              <CommonLink
                href={routes.HOW_TO_USE_ARTICLE}
                target="_blank"
                className={LINK_CLASSNAME}
              >
                外資就活ドットコムを150%活用する方法
              </CommonLink>
              をご一読ください。
            </p>
          </div>

          <div id="e2" className="mt-16">
            <h3>
              <span>Q.</span>
              エントリーしている選考/イベントのキャンセルについて
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              <CommonLink
                href={routes.EVENT}
                target="_blank"
                className={LINK_CLASSNAME}
              >
                {' '}
                エントリーしている選考/イベント
              </CommonLink>
              からキャンセルすることができます。
              <br />
              ※ただし、エントリー後のキャンセルを許可していない選考／イベントについてはできません。
            </p>
          </div>

          <div id="e3" className="mt-16">
            <h3>
              <span>Q.</span>退会したい
            </h3>
            <p>
              <span className="text-notification-1">A.</span>
              <CommonLink href={routes.QUIT} className={LINK_CLASSNAME}>
                こちら
              </CommonLink>
              から退会手続きを行ってください
            </p>
          </div>

          <div className="mt-16 text-right">
            <CommonLink href="#fb-root" className={LINK_CLASSNAME}>
              FAQトップへ
            </CommonLink>
          </div>

          <div className="mt-20 bg-background-1 px-20 py-16">
            ここで解決しない問題、外資就活ドットコムへの質問・ご要望などございましたら、
            <CommonLink
              href={routes.INQUIRY}
              target="_blank"
              className={LINK_CLASSNAME}
            >
              お問い合わせフォーム
            </CommonLink>
            からお問い合わせください。
          </div>
        </Box>
      </Box>
    </>
  );
};
